import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { DialogActions } from './DialogActions';
import { useDiscardGuard } from '../hooks/useDiscardGuard';
import { FormError } from './FormError';
import { Button } from './ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { apiService } from '../services/api';
import { classifyError, humanizeError } from '../lib/errors';
import type { Lens } from '../types/api';
import { lensCreatedToast, validateLensDraft, type LensFieldErrors } from '../lib/lensForm';
import { LensBasicsFields } from './LensBasicsFields';

interface CreateLensDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  accessToken: string;
  /** Whether the caller may create the lens as public. */
  canMakePublic: boolean;
  /** Fired after a successful create, with the created lens: the caller pins it into its list and selects it. */
  onCreated: (lens: Lens) => void;
}

/**
 * Create one lens, without leaving wherever you were.
 *
 * Deliberately NOT the Lenses page's dialog: that one is also its edit dialog
 * and carries a processor-template picker, which is redundant inside a
 * template's own publish wizard — the wizard's lens list is how this template
 * gets attached, and offering a second, contradictory way to do it in a nested
 * popup is worse than offering none. The validation rules the two share live in
 * `lib/lensForm`, which is the part that must not drift.
 *
 * It does not attach anything. It hands the created lens back; the caller puts
 * it at the top of its list and ticks it, with no reload, so the attach still
 * happens exactly once, at submit, with everything else. Its toast is the one
 * acknowledgement.
 */
export const CreateLensDialog: React.FC<CreateLensDialogProps> = ({
  open,
  onOpenChange,
  organizationId,
  accessToken,
  canMakePublic,
  onCreated,
}) => {
  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<LensFieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset on the closed→open transition, so a second open never shows the
  // previous attempt's values or its error.
  useEffect(() => {
    if (!open) return;
    setName('');
    setAlias('');
    setDescription('');
    setIsPublic(false);
    setFieldErrors({});
    setError(null);
  }, [open]);

  const isDirty = Boolean(name.trim() || alias.trim() || description.trim()) || isPublic;
  // Escape, outside click, X and Cancel route through one guard. Its confirm
  // opens above this dialog, which may itself sit above the publish wizard.
  const guard = useDiscardGuard({
    isDirty,
    isBusy: busy,
    onClose: () => onOpenChange(false),
    recordNoun: 'the new lens',
  });

  const handleCreate = async () => {
    const errors = validateLensDraft({ name, alias });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setBusy(true);
      setError(null);
      const created = await apiService.createLens(
        {
          name: name.trim(),
          alias: alias.trim(),
          description: description.trim() || null,
          // Mirrors the Lenses page: without the permission the box is not
          // shown, and the value is forced false rather than sent and refused.
          isPublic: canMakePublic ? isPublic : false,
          processorTemplateIds: [],
        },
        organizationId,
        accessToken,
      );
      toast.success(lensCreatedToast(created.name));
      onCreated(created);
      onOpenChange(false);
    } catch (err: unknown) {
      console.error('Failed to create lens:', err);
      const failure = classifyError(err);
      if (failure.kind === 'conflict' && failure.field === 'alias') {
        setFieldErrors((prev) => ({ ...prev, alias: humanizeError(err) }));
      } else {
        setError(humanizeError(err, "Couldn't create the lens"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      pending={busy}
      onOpenChange={guard.onOpenChange}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Create lens</DialogTitle>
          <DialogDescription>
            A lens groups processor templates so subscribers can reach them by alias. The new lens is
            added to the list and selected.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <LensBasicsFields
            value={{ name, alias, description, isPublic }}
            onChange={(key, value) => {
              if (key === 'name') setName(value as string);
              if (key === 'alias') setAlias(value as string);
              if (key === 'description') setDescription(value as string);
              if (key === 'isPublic') setIsPublic(value as boolean);
              if (key === 'name' || key === 'alias') setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
            }}
            errors={fieldErrors}
            // Mirrors the Lenses page: without the permission the box is not
            // shown, and the value is forced false rather than sent and refused.
            visibility={canMakePublic ? 'edit' : 'hidden'}
            disabled={busy}
          />
        </DialogBody>

        <FormError message={error} />
        <DialogActions>
          <Button type="button" variant="outline" onClick={guard.requestClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleCreate()} loading={busy}>
            Create lens
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
    {guard.guardDialog}
    </>
  );
};
